import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import NewsletterSignup from '../components/NewsletterSignup';

interface QAEntry {
  question: string;
  answer: string[];
  attribution: string;
}

const QA_ENTRIES: QAEntry[] = [
  {
    question: 'How do we email a coach without sounding desperate?',
    answer: [
      'Coaches read the first two lines and the subject. Lead with the one number they can verify — your event, your time or ranking, and where they can check it. “2:04 800m, state finalist, MileSplit link” beats three sentences about passion. If the first line makes them open your athletic profile, the email worked.',
      'What reads as desperate isn’t enthusiasm — it’s asking for something before offering anything to evaluate. Don’t ask “do I have a shot?” in a first email. Attach the film or the results, name one specific reason you’re writing to this program rather than twenty, and ask a question a coach can answer in one line, like whether your event group has open roster spots for your class year.',
      'Then follow up once, two weeks later, with a new result if you have one. Silence after two emails is an answer; a new PR is a reason to write again without apologizing.',
    ],
    attribution: 'From a D1 recruit and the parent of two recruited athletes',
  },
  {
    question: 'What does a real research mentor expect from a 16-year-old?',
    answer: [
      'Not brilliance — reliability. A professor or PhD student taking on a high schooler expects you to show up on schedule, do the reading they assign before asking questions it answers, and be honest immediately when something didn’t work. The students who last in labs are the ones who send the unglamorous weekly update even when the result is “the assay failed twice.”',
      'They also expect a realistic scope. Your first six months are likely cleaning data, running protocols someone else designed, or reproducing a known result. That isn’t hazing — it’s how they learn whether they can trust you with an open question. Students who treat the grunt work as beneath them rarely get the interesting project.',
      'What they do not expect: a publication timeline. If a program promises your kid a paper in a summer, that’s a product, not research.',
    ],
    attribution: 'From a published researcher who started in a university lab at 15',
  },
  {
    question: 'Which activities actually mattered on my application?',
    answer: [
      'Looking at my own file afterward, the two activities that mattered were the ones with externally verifiable results and a story of increasing responsibility — the robotics team I eventually led to a championship, and a job. The five other lines on my activity list were scenery. Readers spend seconds per line; verified depth in one or two places is what survives that skim.',
      'The surprise was what didn’t matter: the expensive summer program, the honor societies, the third club I joined junior year “for the application.” Nothing with a fee and no selection showed any evidence of moving the needle, and our verified case library shows the same pattern across profiles — admits cluster around one deep, checkable commitment, not breadth.',
    ],
    attribution: 'From a mentor admitted to MIT and Berkeley, Einstein Award finalist',
  },
  {
    question: 'How much should we spend on a private coach?',
    answer: [
      'Our parent cost records put private coaching between $60 and $150 an hour depending on the sport and region — but the more useful number is when families started paying. The records that look best in hindsight started private coaching after the kid had plateaued with free team coaching, not before. Paying $120 an hour to teach fundamentals a school coach covers for free was the most-cited regret.',
      'A workable rule from the veteran parents: don’t buy hours, buy a diagnosis. One or two sessions with a good coach to identify the specific gap, a block of training targeted at that gap, then reassess. Open-ended weekly retainers are where the five-figure surprise totals in our dataset came from.',
      'And ask the hindsight question before you sign: if your kid doesn’t get recruited, will this have been worth it for the sport alone? Families who could answer yes spent less and regretted less.',
    ],
    attribution: 'From the true-cost dataset · 38 veteran-parent records',
  },
  {
    question: 'Deferred early — what happens now?',
    answer: [
      'First, decode what a deferral is: not a soft rejection, but a decision to compare you against the regular pool. Your job in the next four weeks is to give them one new data point. That means a short letter of continued interest — a third of a page, not two — with your mid-year grades, one concrete update since November, and a plain statement that you’ll attend if admitted, if that’s true.',
      'What doesn’t help: extra recommendation letters they didn’t ask for, weekly emails, or a parent calling the admissions office. Deferral files get re-read once. One strong update positioned for that re-read beats a drip campaign that marks your file as high-maintenance.',
      'In parallel, finish your regular-decision applications as if the deferral were a no. The students who handle deferrals best emotionally are the ones whose other applications are already done — hedged families negotiate from strength.',
    ],
    attribution: 'From a former admissions reader, 6 years of early-round files',
  },
  {
    question: 'Can she compete internationally and still take five APs?',
    answer: [
      'It’s been done, but every family I know who did it made an explicit trade on paper first. International competition costs 15–25 hours a week in season, plus travel that lands mid-semester. Five APs cost roughly the same. The kids who survived both had schools willing to formalize flexibility — recorded lectures, shifted deadlines, exams taken on the road — arranged in September, not discovered in a crisis in February.',
      'The honest question is what the fifth AP is for. Admissions offices read course rigor in the context of what else the transcript shows; a national-team athlete with four APs and top grades reads as disciplined, while five APs with slipping grades and a withdrawal from the team reads as overreach. In our case library, no admit hinged on the marginal AP — several rejections looked like burnout.',
      'So the answer is usually: yes to the competition, four APs, and a conversation with the counselor now so the schedule is a plan rather than an accident.',
    ],
    attribution: 'From a parent mentor whose daughter competed on a national team through 12th grade',
  },
];

const QADigestPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {/* Header */}
      <header className="section" style={{ paddingBottom: 48 }}>
        <div className="wrap">
          <span className="eyebrow" style={{ display: 'block', marginBottom: 12 }}>
            Q&A digest · free
          </span>
          <h1 style={{ maxWidth: '26ch' }}>Real questions from families, answered by people who lived them.</h1>
          <p style={{ color: 'var(--ink-secondary)', marginTop: 16, maxWidth: '62ch' }}>
            These are actual questions parents and students bring to mentor sessions, answered in
            public by the mentors and veteran parents behind our verified records. New answers go
            out in the weekly email.
          </p>
        </div>
      </header>

      {/* Q&A cards */}
      <section className="section warm" style={{ paddingTop: 48 }}>
        <div className="wrap" style={{ maxWidth: 880 }}>
          {QA_ENTRIES.map((entry) => (
            <div key={entry.question} className="card" style={{ marginBottom: 20, padding: 32 }}>
              <h3 style={{ marginBottom: 14 }}>{entry.question}</h3>
              {entry.answer.map((paragraph, i) => (
                <p
                  key={i}
                  style={{ color: 'var(--ink-secondary)', fontSize: 15, lineHeight: 1.7, marginBottom: 12 }}
                >
                  {paragraph}
                </p>
              ))}
              <p className="meta" style={{ marginTop: 4 }}>{entry.attribution}</p>
              <Link
                className="meta"
                to="/mentors"
                style={{ textDecoration: 'underline', display: 'inline-block', marginTop: 8 }}
              >
                Ask the person behind this answer →
              </Link>
            </div>
          ))}
        </div>
      </section>

      <NewsletterSignup
        variant="band"
        source="qa-digest"
        heading="Get new answers every week."
        subtext="One or two new mentor-answered questions in your inbox each week, alongside the weekly tip — free, no spam, unsubscribe anytime."
      />
    </>
  );
};

export default QADigestPage;
